import type { PlanLimits, SubscriptionPlan } from '@my-roadtrip/types'

const ISO_ANCHOR = '2024-01-01T00:00:00.000Z'

export const FREE_PLAN_LIMITS: PlanLimits = {
  plansPerMonth: 3,
  stopsPerPlan: 10,
  aiCallsPerMonth: 50,
  collaborators: 0,
  storageGB: 0.1
}

const PREMIUM_LIMITS: PlanLimits = {
  plansPerMonth: 50,
  stopsPerPlan: -1,
  aiCallsPerMonth: 1000,
  collaborators: 5,
  storageGB: 1
}

const PRO_LIMITS: PlanLimits = {
  plansPerMonth: -1,
  stopsPerPlan: -1,
  aiCallsPerMonth: 5000,
  collaborators: -1,
  storageGB: 10
}

function resolveStripePrice(planId: string, fallback?: string | null): string | null {
  const envKey = `STRIPE_PRICE_${planId.replace(/-/g, '_').toUpperCase()}`
  const envValue = process.env[envKey]
  if (envValue && envValue.length > 3) {
    return envValue
  }
  return fallback ?? null
}

export function buildDefaultPlans(): SubscriptionPlan[] {
  const now = ISO_ANCHOR
  const plans: SubscriptionPlan[] = [
    {
      id: 'free',
      name: 'Free',
      tier: 'free',
      description: 'Essentials for casual explorers',
      monthlyPrice: 0,
      currency: 'eur',
      billingInterval: 'monthly',
      stripeProductId: null,
      stripePriceId: null,
      features: [
        '3 roadbook generations par mois',
        'Jusqu\'à 10 arrêts par plan',
        'Cartes basiques',
        'Export GPX'
      ],
      limits: FREE_PLAN_LIMITS,
      highlight: 'Parfait pour découvrir',
      badge: 'Gratuit',
      discountMonths: undefined,
      active: true,
      displayOrder: 0,
      createdAt: now,
      updatedAt: now
    },
    {
      id: 'premium',
      name: 'Premium',
      tier: 'premium',
      description: 'Boost complet pour les roadtrips réguliers',
      monthlyPrice: 9.99,
      currency: 'eur',
      billingInterval: 'monthly',
      stripeProductId: null,
      stripePriceId: resolveStripePrice('premium'),
      features: [
        '50 générations par mois',
        'Arrêts illimités',
        'Optimisation avancée',
        'Mode hors ligne',
        'Collaboration (5 personnes)',
        'Couches trafic/météo',
        'Export multi-formats',
        'Support prioritaire'
      ],
      limits: PREMIUM_LIMITS,
      highlight: 'Notre offre la plus populaire',
      badge: 'Premium',
      discountMonths: undefined,
      active: true,
      displayOrder: 1,
      createdAt: now,
      updatedAt: now
    },
    {
      id: 'pro',
      name: 'Pro',
      tier: 'pro',
      description: 'Pour les créateurs et agences',
      monthlyPrice: 19.99,
      currency: 'eur',
      billingInterval: 'monthly',
      stripeProductId: null,
      stripePriceId: resolveStripePrice('pro'),
      features: [
        'Générations illimitées',
        'Modèles IA premium (GPT-4 Turbo)',
        'Optimisation multi-critères',
        'Collaboration illimitée',
        'Accès API',
        'Analytics avancées',
        'White-label',
        'Support 24/7'
      ],
      limits: PRO_LIMITS,
      highlight: 'Pensé pour les équipes exigeantes',
      badge: 'Pro',
      discountMonths: undefined,
      active: true,
      displayOrder: 2,
      createdAt: now,
      updatedAt: now
    },
    {
      id: 'premium_annual',
      name: 'Premium Annuel',
      tier: 'premium',
      description: 'Economisez 2 mois avec l\'engagement annuel',
      monthlyPrice: 99.99,
      currency: 'eur',
      billingInterval: 'annual',
      stripeProductId: null,
      stripePriceId: resolveStripePrice('premium_annual'),
      features: [
        'Identique à Premium',
        '2 mois offerts',
        'Priorité roadmap'
      ],
      limits: PREMIUM_LIMITS,
      highlight: 'Le meilleur rapport qualité/prix',
      badge: '2 mois offerts',
      discountMonths: 2,
      active: true,
      displayOrder: 3,
      createdAt: now,
      updatedAt: now
    }
  ]

  return plans
}

export function findDefaultPlan(planId: string): SubscriptionPlan | undefined {
  return buildDefaultPlans().find(plan => plan.id === planId)
}

export function findDefaultPlanByPriceId(priceId: string): SubscriptionPlan | undefined {
  if (!priceId) return undefined
  return buildDefaultPlans().find(plan => plan.stripePriceId === priceId)
}
